import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { FlightSchoolResult } from '../../shared/models/flight-school-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the Flight School Finder, mirroring {@link FlyingClubFinderResultsListingComponent}.
 * Renders the paged result cards (each linking to the existing `/business/:id` detail page), a filter
 * popover, and relays hover events for map sync.
 */
@Component({
  selector: 'app-flight-school-finder-results-listing',
  templateUrl: './flight-school-finder-results-listing.component.html',
  styleUrls: ['./flight-school-finder-results-listing.component.scss']
})
export class FlightSchoolFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() flightSchools: Array<FlightSchoolResult>;
  @Input() state: State = State.Empty;
  @Input() activeFlightSchool: FlightSchoolResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalFlightSchools = 0;
  @Output() flightSchoolMouseEnter = new EventEmitter<FlightSchoolResult>();
  @Output() flightSchoolMouseLeave = new EventEmitter<FlightSchoolResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('flightSchoolListEl') flightSchoolListEl: ElementRef;
  @ViewChildren('flightSchoolEl', { read: ElementRef }) flightSchoolEls: QueryList<ElementRef>;
  @ViewChild('flightSchoolFiltersPopover') flightSchoolFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to a school's detail page, so that page's
   * "Back" button can return to these exact results (see BusinessPageComponent.goBack). Also clears the
   * airport back-target so the school opened from search doesn't fall back to "Back to <airport>".
   */
  onFlightSchoolSelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onFlightSchoolHover(flightSchool: FlightSchoolResult, hover: boolean) {
    if (hover) {
      this.flightSchoolMouseEnter.emit(flightSchool);
    } else {
      this.flightSchoolMouseLeave.emit(flightSchool);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given flight school.
   */
  getFlightSchoolEl(flightSchool: FlightSchoolResult) {
    const i = this.flightSchools.indexOf(flightSchool);
    if (i < 0) {
      return null;
    }
    const el = this.flightSchoolEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified flight school (used for map hover sync).
   */
  public scrollToFlightSchool(flightSchool: FlightSchoolResult) {
    if (!this.flightSchoolListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.flightSchoolListEl.nativeElement);
    const $el = this.getFlightSchoolEl(flightSchool);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.flightSchoolListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.flightSchoolListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
